import { EasyAlerts } from "@/src/easy-ui/types";
import { stripHtml } from "@/src/shared/utils/html";

interface ClipboardSettings {
  noFlash?: boolean;
  successLabel?: string;
  isHtml?: boolean;
}

/**
 * Copy given +textToCopy+ to system clipboard
 *
 * @param {string} textToCopy - The text or HTML to be copied to the clipboard.
 * @param {Object} [settings] - Optional settings for the clipboard copy operation.
 * @param {boolean} [settings.noFlash=false] - If true, do not show a flash message after copying.
 * @param {string} [settings.successLabel=window.I18n.labelCopied] - Show custom success message in the flash message.
 * @param {boolean} [settings.isHtml=false] - If true, copies the content as HTML.
 * @returns {Promise<boolean>} - Whether the content was copied successfully.
 */
export const copyToClipboard = async (textToCopy: string, settings?: ClipboardSettings): Promise<boolean> => {
  if (!textToCopy) return false;

  const { noFlash, successLabel, isHtml } = getClipboardSettings(settings);

  try {
    await copyContentToClipboard(textToCopy, isHtml);
    if (!noFlash) {
      showSuccessNotification(successLabel);
    }
    return true;
  } catch (error) {
    console.error("Failed to copy content to clipboard", error);
    return false;
  }
};

const getClipboardSettings = ({
  noFlash = false,
  successLabel = window.I18n.labelCopied,
  isHtml = false,
}: ClipboardSettings = {}) => {
  return {
    noFlash,
    successLabel,
    isHtml,
  };
};

const copyContentToClipboard = async (textToCopy: string, isHtml: boolean) => {
  if (isHtml) {
    await copyHtmlToClipboard(textToCopy);
  } else {
    await navigator.clipboard.writeText(textToCopy);
  }
};

const copyHtmlToClipboard = async (htmlContent: string) => {
  const data = [
    new ClipboardItem({
      "text/html": new Blob([htmlContent], { type: "text/html" }),
      "text/plain": new Blob([stripHtml(htmlContent)], { type: "text/plain" }),
    }),
  ];
  await navigator.clipboard.write(data);
};

const showSuccessNotification = (message: string) => {
  try {
    window.EasyUI.showEasyNotification({
      type: EasyAlerts.Success,
      message,
    });
  } catch (error) {
    console.error("Failed to show clipboard success notification", error);
  }
};
